import React from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import { useEffect, useState } from "react";
import EmailLink from "@/components/EmailLink";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import ContactBar from "@/components/ContactBar";

const NAV = [
  { to: "/sell", label: "Sell" },
  { to: "/investors", label: "Investors" },
  { to: "/buyers", label: "Buyers" },
  { to: "/deals", label: "Deals" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-black/85 border-b border-white/10"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img src="/brand/logo-mark.png" alt="Brightside Capital logo" className="h-9 md:h-11 w-auto" width="263" height="375" data-testid="brand-logo" />
          <span className="font-display text-base md:text-lg tracking-tight">Brightside Capital</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide ${isActive ? "text-[#C19B6C]" : "text-neutral-300 hover:text-white"} transition-colors`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/sell"
            data-testid="header-cta-sell"
            className="btn-gold px-5 py-2.5 text-sm font-semibold"
          >
            Sell Your Property
          </Link>
        </nav>
        <button
          aria-label="Toggle menu"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black" data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="text-base text-neutral-200"
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              >
                {n.label}
              </Link>
            ))}
            <Link to="/sell" className="btn-gold text-center px-5 py-3 font-semibold mt-2">
              Sell Your Property
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-black border-t border-white/10 mt-16 md:mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-12 md:py-16 grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-12">
        <div className="md:col-span-2">
          <img src="/brand/logo-full.png" alt="BSC Brightside Capital: Creative Solution to Maximize your Options" className="h-20 md:h-24 w-auto" width="691" height="433" data-testid="footer-logo" />
          <div className="font-display text-lg mt-5">Brightside Capital Ventures LLC</div>
          <p className="text-sm text-neutral-400 mt-2">Roseville/Sacramento, California</p>
          <p className="text-sm text-neutral-400 max-w-md leading-relaxed mt-4">
            Veteran-led real estate investment, redevelopment, and acquisitions. Sacramento-focused.
            Creative deal structures, small multifamily, value-add, ADU and infill opportunities.
          </p>
          <div className="mt-5 text-xs text-neutral-500 leading-relaxed max-w-md">
            Information on this website is for general informational purposes only and does not
            constitute legal, tax, financial, investment, or securities advice. Any
            investment-related discussions are preliminary and subject to appropriate legal
            documentation and compliance review.
          </div>
        </div>
        <div>
          <div className="overline mb-4">Service Areas</div>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li>Roseville/Sacramento, California</li>
            <li>Rocklin, Lincoln, Citrus Heights</li>
            <li>Rancho Cordova, Elk Grove, North Sacramento</li>
            <li>Sacramento County and Placer County</li>
          </ul>
        </div>
        <div>
          <div className="overline mb-4">Contact</div>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li><a href="tel:+19166780058" className="hover:text-[#C19B6C]" data-testid="footer-phone">(916) 678-0058</a></li>
            <li><EmailLink email="acquisitions@brightsidecapital.net" testId="footer-acq-email" /></li>
            <li><EmailLink email="invest@brightsidecapital.net" testId="footer-invest-email" /></li>
            <li>
              <a href="https://www.instagram.com/BrightsideCapitalVentures/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-[#C19B6C]" data-testid="footer-instagram">
                <Instagram size={14} /> @BrightsideCapitalVentures
              </a>
            </li>
          </ul>
          <div className="overline mt-8 mb-3">Legal</div>
          <ul className="space-y-2 text-sm text-neutral-400">
            <li><Link to="/privacy" className="hover:text-[#C19B6C]">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-[#C19B6C]">Terms of Use</Link></li>
            <li><Link to="/admin" className="hover:text-[#C19B6C]">Admin</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} Brightside Capital Ventures LLC. All rights reserved.
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <ContactBar />
      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
